import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCopy, 
  faCheck, 
  faExternalLinkAlt, 
  faScaleBalanced,
  faEnvelope,
  faBuilding,
  faUserShield,
  faInfoCircle,
  faPaperPlane,
  faLink
} from '@fortawesome/free-solid-svg-icons';
import BtnTaratic from '../../components/ui/BtnTaratic';
import { useTranslation } from 'react-i18next';

const LegalSettings: React.FC = () => {
  const { t } = useTranslation();
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Determine Public Frontend Base URL from env vars or fallback to origin
  const getFrontendBaseUrl = (): string => {
    let publicUrl = 
      (import.meta as any).env?.VITE_PUBLIC_URL || 
      (import.meta as any).env?.VITE_APP_URL || 
      (import.meta as any).env?.VITE_FRONTEND_URL;

    if (!publicUrl || typeof publicUrl !== 'string' || publicUrl.trim() === '') {
      publicUrl = window.location.origin;
    }
    
    publicUrl = publicUrl.replace(/\/$/, '');
    return publicUrl.replace(/^http:\/\//, 'https://');
  };

  const frontendBaseUrl = getFrontendBaseUrl();
  const supportEmail = (import.meta as any).env?.VITE_SUPPORT_EMAIL || 'contacto@taratic.com';
  const userDeletionSubject = t('settings.legal.userDeletionSubject', 'Solicitud de Eliminación de Datos - Meta/Instagram');
  const clinicDeletionSubject = t('settings.legal.clinicDeletionSubject', 'Solicitud de Eliminación de Datos de Comercio - Meta/Instagram');

  const handleCopy = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div data-ui="settings-legal-page" className="space-y-8 animate-fade-in pb-10">
      {/* Header Principal */}
      <div data-ui="legal-settings-region-001">
        <h1 className="text-3xl font-black text-white">
          {t('settings.legal.title', 'Legal y Privacidad')}
        </h1>
        <p className="text-white/80 mt-1 text-sm font-medium">
          {t('settings.legal.subtitle', 'Consulta los documentos legales de la plataforma e instrucciones oficiales para la eliminación de datos de usuarios y comercios.')}
        </p>
      </div>

      {/* Sección 1: Documentos Legal y Privacidad */}
      <div data-ui="legal-settings-region-002" className="space-y-4">
        <div data-ui="legal-settings-region-003" className="flex items-center gap-2 text-white font-semibold text-base">
          <FontAwesomeIcon icon={faScaleBalanced} className="text-blue-400" />
          <span>{t('settings.legal.publicPoliciesTitle', 'Políticas y Términos Públicos')}</span>
        </div>

        <div data-ui="legal-settings-region-004" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Política de Privacidad */}
          <div data-ui="legal-settings-region-005" className="bg-taratic-surface/40 border-none p-5 rounded-2xl backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md space-y-3 flex flex-col justify-between">
            <div data-ui="legal-settings-region-006">
              <h3 className="text-base font-semibold text-white">{t('settings.legal.privacyPolicyTitle', 'Política de Privacidad')}</h3>
              <p className="text-xs text-white/90 mt-1 leading-relaxed">
                {t('settings.legal.privacyPolicyDesc', 'Información transparente sobre el tratamiento, almacenamiento y protección de datos personales.')}
              </p>
            </div>
            
            <div data-ui="legal-settings-region-007" className="flex items-center gap-2 pt-2">
              <a 
                href={`${frontendBaseUrl}/privacy`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex-1 flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-black/40 hover:bg-black/60 text-blue-400 hover:text-blue-300 transition-all font-mono text-xs truncate group"
              >
                <FontAwesomeIcon icon={faLink} className="text-white/60 group-hover:text-blue-400 shrink-0" />
                <span className="truncate underline underline-offset-4 decoration-blue-400/30 group-hover:decoration-blue-400 font-medium text-white">
                  {`${frontendBaseUrl}/privacy`}
                </span>
                <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs text-white/60 group-hover:text-blue-400 shrink-0 ml-auto" />
              </a>

              <BtnTaratic
                color={copiedId === 'privacy' ? 'green' : 'ghost-dark'}
                className="h-10 px-3 shrink-0 flex items-center justify-center gap-1.5 text-xs font-medium text-white"
                onClick={() => handleCopy('privacy', `${frontendBaseUrl}/privacy`)}
              >
                <FontAwesomeIcon icon={copiedId === 'privacy' ? faCheck : faCopy} />
                <span>{copiedId === 'privacy' ? t('settings.legal.copied', '¡Copiado!') : t('settings.legal.copy', 'Copiar')}</span>
              </BtnTaratic>
            </div>
          </div>

          {/* Términos y Condiciones */}
          <div data-ui="legal-settings-region-008" className="bg-taratic-surface/40 border-none p-5 rounded-2xl backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md space-y-3 flex flex-col justify-between">
            <div data-ui="legal-settings-region-009">
              <h3 className="text-base font-semibold text-white">{t('settings.legal.termsOfServiceTitle', 'Términos de Servicio')}</h3>
              <p className="text-xs text-white/90 mt-1 leading-relaxed">
                {t('settings.legal.termsOfServiceDesc', 'Condiciones y acuerdos legales de uso del servicio y las integraciones multicanal.')}
              </p>
            </div>

            <div data-ui="legal-settings-region-010" className="flex items-center gap-2 pt-2">
              <a 
                href={`${frontendBaseUrl}/terms`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex-1 flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-black/40 hover:bg-black/60 text-blue-400 hover:text-blue-300 transition-all font-mono text-xs truncate group"
              >
                <FontAwesomeIcon icon={faLink} className="text-white/60 group-hover:text-blue-400 shrink-0" />
                <span className="truncate underline underline-offset-4 decoration-blue-400/30 group-hover:decoration-blue-400 font-medium text-white">
                  {`${frontendBaseUrl}/terms`}
                </span>
                <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs text-white/60 group-hover:text-blue-400 shrink-0 ml-auto" />
              </a>

              <BtnTaratic
                color={copiedId === 'terms' ? 'green' : 'ghost-dark'}
                className="h-10 px-3 shrink-0 flex items-center justify-center gap-1.5 text-xs font-medium text-white"
                onClick={() => handleCopy('terms', `${frontendBaseUrl}/terms`)}
              >
                <FontAwesomeIcon icon={copiedId === 'terms' ? faCheck : faCopy} />
                <span>{copiedId === 'terms' ? t('settings.legal.copied', '¡Copiado!') : t('settings.legal.copy', 'Copiar')}</span>
              </BtnTaratic>
            </div>
          </div>
        </div>
      </div>

      {/* Sección 2: Eliminación de Datos de Usuarios / Pacientes */}
      <div data-ui="legal-settings-region-011" className="space-y-4 pt-2">
        <div data-ui="legal-settings-region-012" className="flex items-center gap-2 text-white font-semibold text-base">
          <FontAwesomeIcon icon={faUserShield} className="text-purple-400" />
          <span>{t('settings.legal.userDeletionSectionTitle', 'Solicitud de Eliminación de Datos de Usuario / Paciente')}</span>
        </div>

        <div data-ui="legal-settings-region-013" className="bg-taratic-surface/40 border-none p-6 rounded-2xl backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md space-y-5">
          <p className="text-xs sm:text-sm text-white/90 leading-relaxed font-normal">
            {t('settings.legal.userDeletionDesc', 'Si eres un usuario final o paciente y deseas solicitar la eliminación completa de tus datos personales recopilados a través de nuestras integraciones con Instagram, Facebook o WhatsApp, debes seguir las siguientes instrucciones:')}
          </p>

          {/* Recuadro de Correo y Asunto */}
          <div data-ui="legal-settings-region-014" className="bg-black/40 p-4 rounded-xl space-y-3">
            <div data-ui="legal-settings-region-015" className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/10 pb-3">
              <span className="text-xs text-white/80 font-medium">{t('settings.legal.step1Email', '1. Correo de destino:')}</span>
              <div data-ui="legal-settings-region-016" className="flex items-center gap-2">
                <code className="text-xs sm:text-sm font-mono text-blue-300 bg-blue-500/10 px-2.5 py-1 rounded-lg">
                  {supportEmail}
                </code>
                <BtnTaratic
                  color={copiedId === 'user-email' ? 'green' : 'ghost-dark'}
                  className="h-8 px-2.5 text-xs text-white"
                  onClick={() => handleCopy('user-email', supportEmail)}
                >
                  <FontAwesomeIcon icon={copiedId === 'user-email' ? faCheck : faCopy} />
                  <span>{copiedId === 'user-email' ? t('settings.legal.copied', '¡Copiado!') : t('settings.legal.copy', 'Copiar')}</span>
                </BtnTaratic>
              </div>
            </div>

            <div data-ui="legal-settings-region-017" className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <span className="text-xs text-white/80 font-medium">{t('settings.legal.step2Subject', '2. Asunto exacto del correo:')}</span>
              <div data-ui="legal-settings-region-018" className="flex items-center gap-2">
                <code className="text-xs font-mono text-purple-300 bg-purple-500/10 px-2.5 py-1 rounded-lg truncate max-w-[260px] sm:max-w-none">
                  {userDeletionSubject}
                </code>
                <BtnTaratic
                  color={copiedId === 'user-subject' ? 'green' : 'ghost-dark'}
                  className="h-8 px-2.5 text-xs text-white"
                  onClick={() => handleCopy('user-subject', userDeletionSubject)}
                >
                  <FontAwesomeIcon icon={copiedId === 'user-subject' ? faCheck : faCopy} />
                  <span>{copiedId === 'user-subject' ? t('settings.legal.copied', '¡Copiado!') : t('settings.legal.copy', 'Copiar')}</span>
                </BtnTaratic>
              </div>
            </div>
          </div>

          {/* Información obligatoria a incluir */}
          <div data-ui="legal-settings-region-019" className="space-y-2">
            <h4 className="text-xs font-semibold text-white uppercase tracking-wider">
              {t('settings.legal.step3UserBodyTitle', '3. Información obligatoria a incluir en el cuerpo del mensaje:')}
            </h4>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-white/90 font-medium">
              <li className="flex items-center gap-2 bg-white/5 p-2.5 rounded-xl">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                <span>{t('settings.legal.userBodyItem1', 'Nombre completo del solicitante')}</span>
              </li>
              <li className="flex items-center gap-2 bg-white/5 p-2.5 rounded-xl">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                <span>{t('settings.legal.userBodyItem2', 'Correo electrónico de contacto')}</span>
              </li>
              <li className="flex items-center gap-2 bg-white/5 p-2.5 rounded-xl">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                <span>{t('settings.legal.userBodyItem3', 'ID de usuario Meta/Instagram o Teléfono')}</span>
              </li>
              <li className="flex items-center gap-2 bg-white/5 p-2.5 rounded-xl">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                <span>{t('settings.legal.userBodyItem4', 'Nombre del comercio/clínica interactuado')}</span>
              </li>
            </ul>
          </div>

          {/* Botones de acción */}
          <div data-ui="legal-settings-region-020" className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <BtnTaratic
              color="purple"
              onClick={() => {
                window.location.href = `mailto:${supportEmail}?subject=${encodeURIComponent(userDeletionSubject)}`;
              }}
              className="flex items-center gap-2 text-xs"
            >
              <FontAwesomeIcon icon={faEnvelope} />
              <span>{t('settings.legal.composeUserEmail', 'Redactar correo de eliminación')}</span>
            </BtnTaratic>

            <a 
              href={`${frontendBaseUrl}/data-deletion/users`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-white hover:text-purple-300 flex items-center gap-1.5 transition-colors font-medium underline underline-offset-4"
            >
              <span>{t('settings.legal.viewFullUserPage', 'Ver página pública completa de instrucciones')}</span>
              <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[10px]" />
            </a>
          </div>
        </div>
      </div>

      {/* Sección 3: Baja y Borrado de Cuenta del Comercio */}
      <div data-ui="legal-settings-region-021" className="space-y-4 pt-2">
        <div data-ui="legal-settings-region-022" className="flex items-center gap-2 text-white font-semibold text-base">
          <FontAwesomeIcon icon={faBuilding} className="text-emerald-400" />
          <span>{t('settings.legal.clinicDeletionSectionTitle', 'Solicitud de Baja y Borrado de Cuenta del Comercio')}</span>
        </div>

        <div data-ui="legal-settings-region-023" className="bg-taratic-surface/40 border-none p-6 rounded-2xl backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md space-y-5">
          <p className="text-xs sm:text-sm text-white/90 leading-relaxed font-normal">
            {t('settings.legal.clinicDeletionDesc', 'Si eres el propietario o administrador de un comercio o clínica y deseas solicitar la baja definitiva de la cuenta, desconexión de canales y eliminación de todos los datos organizacionales, sigue este procedimiento:')}
          </p>

          {/* Recuadro de Correo y Asunto Comercio */}
          <div data-ui="legal-settings-region-024" className="bg-black/40 p-4 rounded-xl space-y-3">
            <div data-ui="legal-settings-region-025" className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/10 pb-3">
              <span className="text-xs text-white/80 font-medium">{t('settings.legal.step1Email', '1. Correo de destino:')}</span>
              <div data-ui="legal-settings-region-026" className="flex items-center gap-2">
                <code className="text-xs sm:text-sm font-mono text-blue-300 bg-blue-500/10 px-2.5 py-1 rounded-lg">
                  {supportEmail}
                </code>
                <BtnTaratic
                  color={copiedId === 'clinic-email' ? 'green' : 'ghost-dark'}
                  className="h-8 px-2.5 text-xs text-white"
                  onClick={() => handleCopy('clinic-email', supportEmail)}
                >
                  <FontAwesomeIcon icon={copiedId === 'clinic-email' ? faCheck : faCopy} />
                  <span>{copiedId === 'clinic-email' ? t('settings.legal.copied', '¡Copiado!') : t('settings.legal.copy', 'Copiar')}</span>
                </BtnTaratic>
              </div>
            </div>

            <div data-ui="legal-settings-region-027" className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <span className="text-xs text-white/80 font-medium">{t('settings.legal.step2Subject', '2. Asunto exacto del correo:')}</span>
              <div data-ui="legal-settings-region-028" className="flex items-center gap-2">
                <code className="text-xs font-mono text-emerald-300 bg-emerald-500/10 px-2.5 py-1 rounded-lg truncate max-w-[260px] sm:max-w-none">
                  {clinicDeletionSubject}
                </code>
                <BtnTaratic
                  color={copiedId === 'clinic-subject' ? 'green' : 'ghost-dark'}
                  className="h-8 px-2.5 text-xs text-white"
                  onClick={() => handleCopy('clinic-subject', clinicDeletionSubject)}
                >
                  <FontAwesomeIcon icon={copiedId === 'clinic-subject' ? faCheck : faCopy} />
                  <span>{copiedId === 'clinic-subject' ? t('settings.legal.copied', '¡Copiado!') : t('settings.legal.copy', 'Copiar')}</span>
                </BtnTaratic>
              </div>
            </div>
          </div>

          {/* Información obligatoria a incluir para comercio */}
          <div data-ui="legal-settings-region-029" className="space-y-2">
            <h4 className="text-xs font-semibold text-white uppercase tracking-wider">
              {t('settings.legal.step3ClinicBodyTitle', '3. Información obligatoria del Administrador a incluir:')}
            </h4>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-white/90 font-medium">
              <li className="flex items-center gap-2 bg-white/5 p-2.5 rounded-xl">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                <span>{t('settings.legal.clinicBodyItem1', 'Nombre completo del Administrador')}</span>
              </li>
              <li className="flex items-center gap-2 bg-white/5 p-2.5 rounded-xl">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                <span>{t('settings.legal.clinicBodyItem2', 'Correo registrado de facturación')}</span>
              </li>
              <li className="flex items-center gap-2 bg-white/5 p-2.5 rounded-xl">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                <span>{t('settings.legal.clinicBodyItem3', 'Nombre comercial de la clínica/empresa')}</span>
              </li>
              <li className="flex items-center gap-2 bg-white/5 p-2.5 rounded-xl">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                <span>{t('settings.legal.clinicBodyItem4', 'ID de la página de Facebook/Instagram vinculada')}</span>
              </li>
            </ul>
          </div>

          {/* Nota de procedimiento */}
          <div data-ui="legal-settings-region-030" className="p-3.5 bg-blue-500/10 rounded-xl text-xs text-blue-200 leading-relaxed flex items-start gap-2.5">
            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-400 text-sm shrink-0 mt-0.5" />
            <span>
              {t('settings.legal.clinicProcedureNotePrefix', 'Un operador de soporte verificará las credenciales del administrador, revocará los tokens de acceso de Meta y procederá con la eliminación completa irreversible de la base de datos y métricas en un plazo máximo de ')}
              <strong>{t('settings.legal.clinicProcedureNoteHighlight', '30 días naturales')}</strong>.
            </span>
          </div>

          {/* Botones de acción comercio */}
          <div data-ui="legal-settings-region-031" className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <BtnTaratic
              color="green"
              onClick={() => {
                window.location.href = `mailto:${supportEmail}?subject=${encodeURIComponent(clinicDeletionSubject)}`;
              }}
              className="flex items-center gap-2 text-xs"
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              <span>{t('settings.legal.composeClinicEmail', 'Redactar solicitud de baja de comercio')}</span>
            </BtnTaratic>

            <a 
              href={`${frontendBaseUrl}/data-deletion/clinics`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-white hover:text-emerald-300 flex items-center gap-1.5 transition-colors font-medium underline underline-offset-4"
            >
              <span>{t('settings.legal.viewFullClinicPage', 'Ver procedimiento de baja para comercios')}</span>
              <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[10px]" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalSettings;

